import {
  CaretDownOutlined,
  CaretUpOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Stack, Typography } from "@strapi/design-system";
import { Box } from "@strapi/design-system/Box";
import { Flex } from "@strapi/design-system/Flex";
import { IconButton } from "@strapi/design-system/IconButton";
import { Pencil } from "@strapi/icons";
import { Button, Modal, Table, message } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getNominees, deleteNominatee, resendEmail } from "../util/API";
import Nominee from "./Nominee";
import NomineeInfo from "./NomineeInfo";

const Nominees = ({
  nomineesData,
  isSecretariatView = false,
  getNomineeSelfElected,
  secretariatPagination,
}) => {
  const { t } = useTranslation();
  const [nominees, setNominees] = useState([...(nomineesData || [])]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedNominee, setSelectedNominee] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();

  const handleResendEmail = async (nominatee) => {
    try {
      const user_id =
        nominatee?.attributes?.users_permissions_user?.data?.id || null;
      const response = await resendEmail({
        data: {
          nominatee_id: user_id,
        },
      });
      if (response.status === 200) {
        messageApi.open({
          type: "success",
          content: response.data.message,
        });
      } else {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
      }
    } catch (error) {
      messageApi.open({
        type: "error",
        content: error.message,
      });
    }
  };

  const toggleExpand = (record) => {
    setSelectedNominee(record);
    setExpandedRowKeys((prev) => {
      const temp = [...prev];
      const index = temp.findIndex((i) => i === record?.id);
      if (index === -1) {
        temp.push(record?.id);
      } else {
        temp.splice(index, 1);
      }
      return temp;
    });
  };
  const columns = [
    {
      title: `${t("str_ordinal_number")}`,
      key: "index",
      render: (value, record, index) => {
        const page =
          isSecretariatView && secretariatPagination
            ? secretariatPagination.current
            : isSecretariatView
              ? 1
              : currentPage;
        const sz =
          isSecretariatView && secretariatPagination
            ? secretariatPagination.pageSize
            : isSecretariatView
              ? 1
              : pageSize;
        return (page - 1) * sz + index + 1;
      },
    },
    {
      title: `${t("str_full_name_nominee")}`,
      dataIndex: ["attributes", "name"],
    },
    {
      title: `${t("str_dob")}`,
      dataIndex: ["attributes", "dob"],
    },
    {
      title: `${t("str_contact_address")}`,
      dataIndex: ["attributes", "address"],
    },
    {
      title: `${t("str_phone_number")}`,
      dataIndex: ["attributes", "phone"],
    },
    {
      title: "Email",
      dataIndex: ["attributes", "email"],
    },
    {
      title: `${t("str_thesis_name")}`,
      dataIndex: ["attributes", "thesis", "data", "attributes", "name"],
    },
    {
      title: `${t("str_nominee_major")}`,
      dataIndex: [
        "attributes",
        "thesis",
        "data",
        "attributes",
        "specialization",
      ],
    },
    {
      title: `${t("str_graduation_school")}`,
      dataIndex: ["attributes", "school"],
    },
    {
      title: `${t("str_status")}`,
      key: "status",
      render: (value, record) => {
        if (
          record?.attributes?.users_permissions_user?.data?.attributes
            .confirmed === false
        ) {
          if (
            !record?.attributes?.users_permissions_user.data.attributes
              .token_expired_at
          ) {
            return (
              <Button
                onClick={() => handleResendEmail(record)}
                type="link"
                style={{ paddingLeft: "0" }}
              >
                {t("str_resend_email")}
              </Button>
            );
          }
          const today = dayjs();
          const expired_date = dayjs(
            record?.attributes?.users_permissions_user.data.attributes
              .token_expired_at,
          );

          if (today.isAfter(expired_date) && isSecretariatView)
            return (
              <Button
                onClick={() => handleResendEmail(record)}
                type="link"
                style={{ paddingLeft: "0" }}
              >
                {t("str_resend_email")}
              </Button>
            );

          return (
            <Typography variant="omega" fontWeight="bold">
              {t("str_not_confirm")}
            </Typography>
          );
        }

        return (
          <Typography variant="omega" fontWeight="bold">
            {t("str_confirmed")}
          </Typography>
        );
      },
    },
    {
      title: "",
      key: "action",
      render: (value, record) => (
        <Flex>
          <>
            <Stack spacing={2} horizontal>
              <IconButton
                onClick={() => handleShowNomineeDetail(record)}
                label="View detail"
                noBorder
                icon={<EyeOutlined />}
              />
              {!isSecretariatView && (
                <IconButton
                  onClick={() => toggleExpand(record)}
                  label="Edit"
                  noBorder
                  icon={<Pencil />}
                />
              )}
            </Stack>
          </>
        </Flex>
      ),
    },
    {
      title: "",
      key: "action",
      render: (value, record) => {
        if (isSecretariatView) {
          return (
            <Flex>
              <Box paddingLeft={1}>
                <IconButton
                  onClick={async () => {
                    await deleteNominatee(record.id);
                    const data = await getNomineeSelfElected();
                    setNominees(data);
                  }}
                  label="Delete"
                  noBorder
                  icon={<DeleteOutlined />}
                />
              </Box>
            </Flex>
          );
        }
        return <></>;
      },
    },
  ];

  useEffect(() => {
    if (!isSecretariatView) fetchNominees();
  }, [isSecretariatView]);

  useEffect(() => {
    if (!isSecretariatView) return;
    setNominees([...(nomineesData || [])]);
  }, [isSecretariatView, nomineesData]);

  const fetchNominees = async () => {
    try {
      const response = await getNominees(currentPage);
      setNominees(response.data?.data);
      setTotalRows(response.data?.meta.total || 0);
    } catch (error) {
      console.error(error);
      // TODO: Handle error while fetching profile
    }
  };

  const handleCreateClick = () => {
    setShowCreateForm(true);
  };

  const handleShowNomineeDetail = (record) => {
    setSelectedNominee(record);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setSelectedNominee({});
    setShowCreateForm(false);
    fetchNominees();
  };

  return (
    <>
      {contextHolder}
      {!isSecretariatView && !showCreateForm && (
        <div style={{ marginBottom: "15px" }}>
          <Button onClick={handleCreateClick}>{t("str_add_nominee")}</Button>
          <br />
        </div>
      )}
      {!showCreateForm && !isSecretariatView && (
        <Table
          rowKey="id"
          columns={columns}
          expandable={{
            onExpand: (expanded, record) => {
              setSelectedNominee(record);
              toggleExpand(record);
            },
            expandedRowRender: (record) => (
              <Nominee
                mode="UPDATE"
                data={selectedNominee}
                handleOnClose={handleCloseDetail}
              />
            ),
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <CaretUpOutlined onClick={(e) => onExpand(record, e)} />
              ) : (
                <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
              ),
            expandedRowKeys: expandedRowKeys,
          }}
          dataSource={nominees}
          scroll={{ x: "1000px", y: 480 }}
          pagination={{
            current: currentPage,
            size: pageSize,
            onChange: setCurrentPage,
          }}
        />
      )}
      {isSecretariatView && (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={nominees}
          scroll={{ x: "1000px", y: 480 }}
          pagination={
            secretariatPagination
              ? {
                  current: secretariatPagination.current,
                  pageSize: secretariatPagination.pageSize,
                  total: secretariatPagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: secretariatPagination.pageSizeOptions,
                  onChange: secretariatPagination.onChange,
                }
              : false
          }
        />
      )}
      {showCreateForm && (
        <Nominee mode="CREATE" handleOnClose={handleCloseDetail} />
      )}

      <Modal
        title={t("str_nominated_candidate_list")}
        centered
        open={showDetailModal}
        onOk={() => setShowDetailModal(false)}
        onCancel={() => setShowDetailModal(false)}
        zIndex={4}
        cancelButtonProps={{ style: { display: "none" } }}
        width={1000}
      >
        <NomineeInfo userInfo={selectedNominee} isSecretariatView={true} />
      </Modal>
    </>
  );
};

export default Nominees;
